// Staff spells view - spell management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/**
 * Staff spells view component for managing spells
 */
class StaffSpellsView extends BaseComponent {
  static properties = {
    _spells: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _spellToDelete: { type: String, state: true },
    _levelFilter: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Spell>} */
    this._spells = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._spellToDelete = null;
    this._levelFilter = '';
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadSpells();
  }

  async _loadSpells() {
    this._loading = true;
    this._error = null;

    try {
      this._spells = await api.get('/api/staff/spells');
    } catch (err) {
      console.error('Failed to load spells:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load spells';
    } finally {
      this._loading = false;
    }
  }

  get _filteredSpells() {
    if (!this._levelFilter) return this._spells;
    const level = parseInt(this._levelFilter, 10);
    return this._spells.filter((s) => s.level === level);
  }

  _goToNewSpell() {
    router.navigate('/staff/spells/new');
  }

  /** @param {string} slug */
  _editSpell(slug) {
    router.navigate(`/staff/spells/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteSpell(slug) {
    this._spellToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._spellToDelete) return;

    try {
      await api.delete(`/api/staff/spells/${this._spellToDelete}`);
      this._spellToDelete = null;
      this._showDeleteModal = false;
      await this._loadSpells();
    } catch (err) {
      console.error('Failed to delete spell:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete spell';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._spellToDelete = null;
  }

  /** @param {number} level */
  _formatLevel(level) {
    if (level === 0) return 'Cantrip';
    if (level === 1) return '1st Level';
    if (level === 2) return '2nd Level';
    if (level === 3) return '3rd Level';
    return `${level}th Level`;
  }

  /**
   * @param {string} description
   * @param {number} [maxLength]
   */
  _truncateDescription(description, maxLength = 100) {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  /** @param {Event} e */
  _handleLevelChange(e) {
    this._levelFilter = /** @type {HTMLSelectElement} */ (e.target).value;
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Spells</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Spells</h2>
        <div class="d-flex gap-2 align-items-center">
          <select
            class="form-select form-select-sm"
            style="width: auto;"
            .value="${this._levelFilter}"
            @change="${this._handleLevelChange}"
          >
            <option value="">All Levels</option>
            <option value="0">Cantrips</option>
            <option value="1">1st Level</option>
            <option value="2">2nd Level</option>
            <option value="3">3rd Level</option>
            <option value="4">4th Level</option>
            <option value="5">5th Level</option>
            <option value="6">6th Level</option>
            <option value="7">7th Level</option>
            <option value="8">8th Level</option>
            <option value="9">9th Level</option>
          </select>
          <button class="btn btn-primary" @click="${this._goToNewSpell}">Create Spell</button>
        </div>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._filteredSpells.length === 0
            ? html` <p class="text-muted">No spells yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._filteredSpells.map(
                  (spell) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${spell.name}</h6>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editSpell(spell.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteSpell(spell.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${spell.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(spell.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge bg-info">${this._formatLevel(spell.level || 0)}</span>
                              <span class="badge bg-secondary">v${spell.version || 1}</span>
                              ${
                                spell.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                            </div>
                          </div>

                          ${
                            spell.description
                              ? html`
                                <p class="small text-muted mb-0">${this._truncateDescription(spell.description)}</p>
                              `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Spell</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this spell? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-spells-view', StaffSpellsView);
